'use client';

import { type ChangeEvent } from 'react';
import { Star, MapPin, Locate } from 'lucide-react';
import { cn } from '@/core/utils/cn';
import { Card, Checkbox, Select } from '@/shared/ui';
import type { FilterState } from '../types/hotels.types';
import {
  PROPERTY_TYPES,
  POPULAR_LOCATIONS,
  RADIUS_OPTIONS,
  DEFAULT_RADIUS_KM,
  AMENITY_FILTER_OPTIONS,
  RATING_FILTER_OPTIONS,
} from './hotels.data';
import { useTranslation } from '@/core/i18n';

export type { FilterState };

/* -------------------------------------------------------------------------- */
/*  Star rating display                                                       */
/* -------------------------------------------------------------------------- */

function StarRating({ count }: { count: number }) {
  return (
    <span className="inline-flex items-center gap-0.5">
      {Array.from({ length: count }).map((_, i) => (
        <Star key={i} className="h-3 w-3 fill-hms-gold text-hms-gold" />
      ))}
    </span>
  );
}

/* -------------------------------------------------------------------------- */
/*  Props                                                                      */
/* -------------------------------------------------------------------------- */

interface FilterPanelProps {
  filters: FilterState;
  onChange: (f: FilterState) => void;
  className?: string;
}

/* -------------------------------------------------------------------------- */
/*  SearchFilterPanel                                                          */
/* -------------------------------------------------------------------------- */

export function SearchFilterPanel({
  filters,
  onChange,
  className,
}: FilterPanelProps) {
  const { t } = useTranslation();

  const toggleStar = (star: number) => {
    const next = filters.starRatings.includes(star)
      ? filters.starRatings.filter((s) => s !== star)
      : [...filters.starRatings, star];
    onChange({ ...filters, starRatings: next });
  };

  const toggleType = (type: string) => {
    const next = filters.propertyTypes.includes(type)
      ? filters.propertyTypes.filter((t) => t !== type)
      : [...filters.propertyTypes, type];
    onChange({ ...filters, propertyTypes: next });
  };

  const toggleAmenity = (amenity: string) => {
    const next = filters.amenities.includes(amenity)
      ? filters.amenities.filter((a) => a !== amenity)
      : [...filters.amenities, amenity];
    onChange({ ...filters, amenities: next });
  };

  const handleLocationChange = (e: ChangeEvent<HTMLSelectElement>) => {
    onChange({
      ...filters,
      searchLocationSlug: e.target.value,
      radiusKm: e.target.value ? (filters.radiusKm || DEFAULT_RADIUS_KM) : 0,
    });
  };

  const handleRadiusChange = (e: ChangeEvent<HTMLSelectElement>) => {
    onChange({ ...filters, radiusKm: parseInt(e.target.value, 10) || DEFAULT_RADIUS_KM });
  };

  const handleClearLocation = () => {
    onChange({ ...filters, searchLocationSlug: '', radiusKm: 0 });
  };

  const handleRatingChange = (e: ChangeEvent<HTMLSelectElement>) => {
    onChange({ ...filters, minRating: parseFloat(e.target.value) || 0 });
  };

  const handleResetAll = () => {
    onChange({
      minPrice: '', maxPrice: '', starRatings: [], propertyTypes: [],
      searchLocationSlug: '', radiusKm: 0, amenities: [], minRating: 0,
    });
  };

  const activeCount =
    (filters.searchLocationSlug ? 1 : 0) +
    (filters.minPrice || filters.maxPrice ? 1 : 0) +
    filters.starRatings.length +
    filters.propertyTypes.length +
    filters.amenities.length +
    (filters.minRating > 0 ? 1 : 0);

  return (
    <div className={cn('space-y-5', className)}>
      <Card padding="md" className="space-y-5">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-hms-text">{t('hotelSearch.filters')}</h3>
          {activeCount > 0 && (
            <span
              id="hotels-search-filter-active-count"
              className="rounded-md bg-hms-gold/15 px-2 py-0.5 text-[10px] font-semibold text-hms-gold"
            >
              {activeCount} {t('hotelSearch.active')}
            </span>
          )}
        </div>

        {/* ---- Location & Radius ---- */}
        <fieldset className="space-y-2.5">
          <legend className="text-[11px] font-semibold uppercase tracking-wider text-hms-textDim">
            <span className="inline-flex items-center gap-1">
              <Locate className="h-3 w-3" />
              {t('hotelSearch.locationSearch')}
            </span>
          </legend>
          <Select
            id="hotels-search-filter-location"
            value={filters.searchLocationSlug}
            onChange={handleLocationChange}
            placeholder={t('hotelSearch.allLocations')}
            options={POPULAR_LOCATIONS.map((loc) => ({ label: loc.name, value: loc.slug }))}
          />

          {filters.searchLocationSlug && (
            <div className="space-y-2">
              <Select
                id="hotels-search-filter-radius"
                value={String(filters.radiusKm || DEFAULT_RADIUS_KM)}
                onChange={handleRadiusChange}
                options={RADIUS_OPTIONS.map((opt) => ({ label: `${opt.label} ${t('hotelSearch.radius')}`, value: String(opt.value) }))}
              />
              <div className="flex items-center gap-1.5 rounded-md bg-hms-teal/10 px-2.5 py-1.5 text-[10px] text-hms-teal">
                <MapPin className="h-3 w-3 shrink-0" />
                <span className="font-medium">
                  {POPULAR_LOCATIONS.find((l) => l.slug === filters.searchLocationSlug)?.name}
                </span>
                <span className="text-hms-textDim">
                  {t('hotelSearch.withinKm', { n: String(filters.radiusKm || DEFAULT_RADIUS_KM) })}
                </span>
                <button
                  id="hotels-search-filter-clear-location"
                  type="button"
                  onClick={handleClearLocation}
                  className="ml-auto rounded p-0.5 text-hms-textDim hover:text-hms-text"
                  aria-label="Clear location filter"
                >
                  <svg className="h-3 w-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M18 6L6 18M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
          )}
        </fieldset>

        {/* ---- Price Range ---- */}
        <fieldset className="space-y-2">
          <legend className="text-[11px] font-semibold uppercase tracking-wider text-hms-textDim">
            {t('hotelSearch.priceRange')}
          </legend>
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-xs text-hms-textDim">
                &#x20B9;
              </span>
              <input
                id="hotels-search-filter-min-price"
                type="number"
                min={0}
                placeholder={t('hotelSearch.min')}
                value={filters.minPrice}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  onChange({ ...filters, minPrice: e.target.value })
                }
                className={cn(
                  'h-9 w-full rounded-lg border border-hms-border bg-hms-surface pl-7 pr-2',
                  'text-xs text-hms-text placeholder:text-hms-textDim',
                  'outline-none focus:border-hms-gold/50 focus:ring-1 focus:ring-hms-gold/30',
                )}
              />
            </div>
            <span className="text-[10px] text-hms-textDim">&ndash;</span>
            <div className="relative flex-1">
              <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-xs text-hms-textDim">
                &#x20B9;
              </span>
              <input
                id="hotels-search-filter-max-price"
                type="number"
                min={0}
                placeholder={t('hotelSearch.max')}
                value={filters.maxPrice}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  onChange({ ...filters, maxPrice: e.target.value })
                }
                className={cn(
                  'h-9 w-full rounded-lg border border-hms-border bg-hms-surface pl-7 pr-2',
                  'text-xs text-hms-text placeholder:text-hms-textDim',
                  'outline-none focus:border-hms-gold/50 focus:ring-1 focus:ring-hms-gold/30',
                )}
              />
            </div>
          </div>
        </fieldset>

        {/* ---- Star Rating ---- */}
        <fieldset className="space-y-2">
          <legend className="text-[11px] font-semibold uppercase tracking-wider text-hms-textDim">
            {t('hotelSearch.starRating')}
            {filters.starRatings.length > 0 && (
              <span className="ml-1.5 text-hms-gold">({filters.starRatings.length})</span>
            )}
          </legend>
          {[5, 4, 3].map((star) => (
            <div key={star} className="flex items-center gap-2.5 text-xs text-hms-textSub hover:text-hms-text">
              <Checkbox
                id={`hotels-search-filter-star-${star}`}
                checked={filters.starRatings.includes(star)}
                onChange={() => toggleStar(star)}
              />
              <StarRating count={star} />
              <span>{star} {t('hotelSearch.star')}</span>
            </div>
          ))}
        </fieldset>

        {/* ---- Property Type ---- */}
        <fieldset className="space-y-2">
          <legend className="text-[11px] font-semibold uppercase tracking-wider text-hms-textDim">
            {t('hotelSearch.propertyType')}
            {filters.propertyTypes.length > 0 && (
              <span className="ml-1.5 text-hms-gold">({filters.propertyTypes.length})</span>
            )}
          </legend>
          {PROPERTY_TYPES.map((type) => (
            <Checkbox
              key={type}
              id={`hotels-search-filter-type-${type.toLowerCase()}`}
              checked={filters.propertyTypes.includes(type)}
              onChange={() => toggleType(type)}
              label={type}
            />
          ))}
        </fieldset>

        {/* ---- Amenities ---- */}
        <fieldset className="space-y-2">
          <legend className="text-[11px] font-semibold uppercase tracking-wider text-hms-textDim">
            {t('hotelSearch.amenities')}
            {filters.amenities.length > 0 && (
              <span className="ml-1.5 text-hms-gold">({filters.amenities.length})</span>
            )}
          </legend>
          {AMENITY_FILTER_OPTIONS.map((amenity) => (
            <Checkbox
              key={amenity}
              id={`hotels-search-filter-amenity-${amenity.toLowerCase().replace(/[^a-z]/g, '')}`}
              checked={filters.amenities.includes(amenity)}
              onChange={() => toggleAmenity(amenity)}
              label={amenity}
            />
          ))}
        </fieldset>

        {/* ---- Guest Rating ---- */}
        <fieldset className="space-y-2">
          <legend className="text-[11px] font-semibold uppercase tracking-wider text-hms-textDim">
            {t('hotelSearch.guestRating')}
          </legend>
          <Select
            id="hotels-search-filter-rating"
            value={String(filters.minRating)}
            onChange={handleRatingChange}
            options={RATING_FILTER_OPTIONS.map((opt) => ({ label: opt.label, value: String(opt.value) }))}
          />
        </fieldset>

        {/* ---- Reset All ---- */}
        {activeCount > 0 && (
          <button
            id="hotels-search-filter-reset"
            type="button"
            onClick={handleResetAll}
            className={cn(
              'flex h-9 w-full items-center justify-center gap-2 rounded-lg',
              'border border-hms-border text-xs font-medium text-hms-textSub',
              'transition-colors hover:border-hms-borderLt hover:text-hms-text',
            )}
          >
            <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M1 4v6h6M23 20v-6h-6" />
              <path d="M20.49 9A9 9 0 005.64 5.64L1 10m22 4l-4.64 4.36A9 9 0 013.51 15" />
            </svg>
            {t('hotelSearch.resetAllFilters')}
          </button>
        )}
      </Card>
    </div>
  );
}
